import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api, isTokenValid, clearTokenFromStorage, triggerLogoutEvent } from '../services/api'

interface Admin {
  id: string
  email: string
  name: string
}

interface AuthContextType {
  admin: Admin | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
  checkAuth: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Função para verificar autenticação com o backend
  const checkAuth = useCallback(async (): Promise<boolean> => {
    const storedToken = localStorage.getItem('admin_token')
    const storedAdmin = localStorage.getItem('admin_data')

    console.log('[AUTH] Verificando autenticação...')
    console.log('[AUTH] Token presente:', !!storedToken)
    console.log('[AUTH] Admin data presente:', !!storedAdmin)

    // Se não tem token, não está autenticado
    if (!storedToken) {
      console.log('[AUTH] ❌ Nenhum token encontrado')
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
      return false
    }

    if (!isTokenValid(storedToken)) {
      console.log('[AUTH] ❌ Token inválido ou expirado (validação local)')
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
      triggerLogoutEvent()
      return false
    }

    try {
      console.log('[AUTH] Validando token com o backend...')
      const adminData = await api.getMe(storedToken)
      console.log('[AUTH] ✅ Token válido! Admin:', adminData.email)
      setToken(storedToken)
      setAdmin(adminData)
      setIsLoading(false)
      return true
    } catch (error) {
      console.error('[AUTH] ❌ Erro ao validar token no backend:', error)
      // Token inválido no backend
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
      triggerLogoutEvent()
      return false
    }
  }, [])

  // Verificar autenticação ao carregar
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Listener para logout automático por eventos
  useEffect(() => {
    const handleLogoutEvent = () => {
      console.log('[AUTH] Evento de logout recebido')
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
    }

    window.addEventListener('auth:logout', handleLogoutEvent)

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent)
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    console.log('[AUTH] Iniciando login...')
    setIsLoading(true)
    
    try {
      const response = await api.login(email, password)
      
      console.log('[AUTH] Login bem-sucedido, salvando dados...')
      localStorage.setItem('admin_token', response.token)
      localStorage.setItem('admin_data', JSON.stringify(response.admin))
      
      setToken(response.token)
      setAdmin(response.admin)
      console.log('[AUTH] ✅ Login concluído')
    } catch (error) {
      console.error('[AUTH] ❌ Erro no login:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    console.log('[AUTH] Iniciando logout...')
    setIsLoading(true)
    
    try {
      const currentToken = token || localStorage.getItem('admin_token')
      if (currentToken) {
        try {
          await api.logout(currentToken)
          console.log('[AUTH] Logout realizado no backend')
        } catch (error) {
          console.error('[AUTH] Erro no logout da API:', error)
        }
      }
    } finally {
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
      console.log('[AUTH] ✅ Logout concluído')
    }
  }, [token])

  // Valor memoizado
  const value = React.useMemo(() => ({
    admin,
    token,
    isLoading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!token && !!admin && !isLoading
  }), [admin, token, isLoading, login, logout, checkAuth])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}